import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Copy, ExternalLink, Radio, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Pixel } from '@/types/pixel';
import { PixelActionMenu } from './PixelActionMenu';
import { PixelStatusBadge } from './PixelStatusBadge';
import { PixelHealthBadge } from './PixelHealthBadge';
import { toast } from '@/hooks/use-toast';

interface PixelTableDesktopProps {
  pixels: Pixel[];
  onRemove: (pixel: Pixel) => void;
}

// Truncar token elegantemente (estilo Stripe)
const truncateToken = (token: string) => {
  if (token.length <= 12) return token;
  return `${token.slice(0, 4)}…${token.slice(-4)}`;
};

// Formatar data relativa
const formatRelativeDate = (dateString?: string) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInHours < 1) return 'agora';
  if (diffInHours < 24) return `há ${diffInHours}h`;
  if (diffInDays === 1) return 'ontem';
  if (diffInDays < 7) return `há ${diffInDays}d`;
  return format(date, "dd MMM", { locale: getInterfaceDateLocale() });
};

export function PixelTableDesktop({ pixels, onRemove }: PixelTableDesktopProps) {
  useLanguage();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [visibleTokens, setVisibleTokens] = useState<Set<string>>(new Set());

  const toggleTokenVisibility = (pixelId: string) => {
    setVisibleTokens(prev => {
      const next = new Set(prev);
      if (next.has(pixelId)) {
        next.delete(pixelId);
      } else {
        next.add(pixelId);
      }
      return next;
    });
  };

  const copyToken = async (pixel: Pixel) => {
    try {
      await navigator.clipboard.writeText(pixel.token);
      setCopiedId(pixel.id);
      toast({
        title: translateInterfaceText("Copiado!"),
        description: translateInterfaceText("Token copiado para a área de transferência."),
      });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast({
        title: translateInterfaceText("Erro ao copiar"),
        description: translateInterfaceText("Não foi possível copiar o token."),
        variant: "destructive",
      });
    }
  };

  const getPlatformIcon = (platform: string) => {
    return <Radio className="w-4 h-4 text-[#437DFF]" />;
  };

  const platformLabel = (pixel: Pixel) => {
    // Fonte oficial: company.name; fallback para platform resolvida no hook
    const company = pixel.company?.name;
    if (company) return company;
    if (pixel.platform === 'google-ads') return translateInterfaceText("Google Ads");
    if (pixel.platform === 'tiktok') return 'TikTok';
    return translateInterfaceText("Meta Pixel");
  };

  return (
    <TooltipProvider>
      <div className="border border-[#E5E7EB] rounded-xl overflow-hidden bg-white">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-base font-semibold text-[#111]">{translateInterfaceText("Pixels Cadastrados")}</h3>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E7EB] hover:bg-transparent">
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("Plataforma")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("Pixel")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("URL Site")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("Vídeo / Funil")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("Eventos enviados")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide">{translateInterfaceText("Status")}</TableHead>
              <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wide w-[60px]">{translateInterfaceText("Ações")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pixels.map((pixel) => (
              <TableRow
                key={pixel.id}
                className="border-[#E5E7EB] transition-colors hover:bg-[#F8FAFC] group"
              >
                {/* Coluna Plataforma (fonte: company, nunca URL-sniffing) */}
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                      {getPlatformIcon(pixel.platform)}
                    </div>
                    <span className="text-sm font-medium text-[#111]">{platformLabel(pixel)}</span>
                  </div>
                </TableCell>

                {/* Coluna Pixel - Token estilo Stripe */}
                <TableCell>
                  <div className="flex items-center gap-2">
                      <span className="text-sm text-[#111] font-mono">
                        {visibleTokens.has(pixel.id) ? truncateToken(pixel.token) : '••••••••'}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => toggleTokenVisibility(pixel.id)}
                              className="text-[#64748B] hover:text-[#437DFF] transition-colors p-1 rounded hover:bg-blue-50"
                            >
                              {visibleTokens.has(pixel.id) ? (
                                <EyeOff className="w-3.5 h-3.5" />
                              ) : (
                                <Eye className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{visibleTokens.has(pixel.id) ? translateInterfaceText("Ocultar token") : translateInterfaceText("Mostrar token")}</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => copyToken(pixel)}
                              className="text-[#64748B] hover:text-[#437DFF] transition-colors p-1 rounded hover:bg-blue-50"
                            >
                              {copiedId === pixel.id ? (
                                <CheckCircle className="w-3.5 h-3.5 text-[#437DFF]" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{translateInterfaceText("Copiar token")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                  </div>
                </TableCell>

                {/* Coluna URL Site */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#111] truncate max-w-[200px]">
                      {pixel.siteUrl}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={pixel.siteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#64748B] hover:text-[#437DFF] transition-colors p-1 rounded hover:bg-blue-50 focus:ring-2 focus:ring-[#437DFF] focus:ring-offset-1"
                          aria-label={translateInterfaceText("Abrir URL em nova aba")}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{translateInterfaceText("Abrir em nova aba")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>

                {/* Coluna Vídeo */}
                <TableCell>
                  <div className="flex items-center gap-3">
                    {pixel.videoThumbnail ? (
                      <img
                        src={pixel.videoThumbnail}
                        alt={pixel.videoName}
                        className="w-10 h-7 rounded object-cover"
                      />
                    ) : (
                      <div className="w-10 h-7 rounded bg-[#F1F5F9] flex items-center justify-center">
                        <Radio className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <span className="text-sm text-[#111] truncate max-w-[150px]">
                      {pixel.flowId ? translateInterfaceText("Funil: {value0}", { value0: (pixel.videoName) }) : pixel.videoName}
                    </span>
                  </div>
                </TableCell>

                {/* Coluna Eventos Enviados */}
                <TableCell>
                  <span className="text-sm text-[#111]">
                    {pixel.eventsSent || '—'}
                  </span>
                </TableCell>

                {/* Coluna Status */}
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <PixelStatusBadge status={pixel.status} />
                    {pixel.active && <PixelHealthBadge health={pixel.health} />}
                  </div>
                </TableCell>

                {/* Coluna Ações */}
                <TableCell>
                  <PixelActionMenu
                    pixel={pixel}
                    onRemove={onRemove}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
